import {
  Checkbox,
  css,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { LoadingSpinner } from 'src/components/Loading';
import { StepContent } from 'src/components/StepElements';
import { Toast } from 'src/components/Toast';
import { device } from 'src/constants/mediaQueries';
import { FormFooter } from 'src/pages/CreateNewProductPage/CreateProductForm/CreateProductFormFooter';
import { useTenant } from 'src/providers/Tenant';
import { getCADJob, patchCADJob, ZipFileContent } from 'src/services/productInventory';

interface GeneralAssemblyPageRedirectState {
  successMessage?: {
    title: string;
    body: string;
  };
}
export default function GeneralAssemblyPage() {
  const { t } = useTranslation('createProduct');
  const { accountId } = useTenant();
  const location = useLocation();
  const isMobile = useMediaQuery(device.mobileOrTablet);

  const searchParams = new URLSearchParams(location?.search);
  const runID = searchParams.get('runID');
  const productName = searchParams.get('productName');

  const { successMessage } = (location.state as GeneralAssemblyPageRedirectState) ?? {};

  const {
    isLoading,
    isError,
    data: files,
  } = useQuery(['getCadRunInfo', runID], /* istanbul ignore next */ () => getCADJob(runID!, accountId));

  const {
    mutate,
    isError: isErrorPatch,
    isLoading: isLoadingPatch,
    isSuccess: patchingComplete,
  } = useMutation(['patchCadRunInfo'], (path: string) =>
    patchCADJob(runID!, accountId, { selectedGeneralAssembly: path })
  );

  const [selectedFile, setSelectedFile] = useState<ZipFileContent | undefined>();

  const handleChange = (file: ZipFileContent) => (event: React.ChangeEvent<HTMLInputElement>) =>
    event.target.checked ? setSelectedFile(file) : setSelectedFile(undefined);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(selectedFile!.path); // selected file has to be there in order to click the button
  };

  const covertByteToMB = (bytes: number) => (bytes / 1000000).toFixed(2);

  if (patchingComplete) {
    return <Redirect to={`/create-product/cad/processing?runID=${runID}&productName=${productName}`} />;
  }

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <StepContent>
          <div>
            <FormLabelStyle>
              <TitleWithSubTitle>
                <Title>{t('assemblyFiles')}</Title>
              </TitleWithSubTitle>
              {isMobile ? /* istanbul ignore next */ null : <FileSizeLabel>{t('size')}</FileSizeLabel>}
            </FormLabelStyle>
            <FormDivider orientation="horizontal" />
            {isLoading ? (
              <LoadingSpinner data-testid="loading-spinner" />
            ) : (
              <CheckboxFields>
                {files ? (
                  files.fileDetails
                    .sort((a, b) => b.size - a.size)
                    .map((file) => {
                      const checked = selectedFile?.path === file.path;
                      return (
                        <CheckboxField key={file.path} checked={checked}>
                          <CheckboxGroup
                            control={<Checkbox name={file.path} onChange={handleChange(file)} checked={checked} />}
                            label={file.path}
                          />
                          {isMobile ? /* istanbul ignore next */ null : (
                            <FileSize>{covertByteToMB(file.size)}Mb</FileSize>
                          )}
                        </CheckboxField>
                      );
                    })
                ) : (
                  /* istanbul ignore next */
                  <NoFilesFound>{t('noFiles')}</NoFilesFound>
                )}
              </CheckboxFields>
            )}
          </div>
        </StepContent>
        <FormFooter
          buttonDisabled={!selectedFile || isLoadingPatch}
          buttonI18nText="nextStep"
          isLoading={isLoadingPatch}
          backButton={false}
        />
      </Form>
      <Toast
        dependency={isError}
        severity="error"
        title={`${t('errorGettingFiles')}`}
        data-testId="toast-notification"
      />
      <Toast
        dependency={isErrorPatch}
        severity="error"
        title={`${t('errorSelectingGeneralAssembly')}`}
        data-testId="toast-notification"
      />
      <Toast
        dependency={successMessage}
        severity="success"
        title={successMessage?.title!}
        message={successMessage?.body}
        autoHideDuration={null}
      />
    </>
  );
}

const Form = styled('form')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const FormLabelStyle = styled(FormLabel)`
  text-align: left;
  width: 100%;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const NoFilesFound = styled('div')`
  margin: auto;
  padding: 1rem;
`;

const CheckboxGroup = styled(FormControlLabel)`
  background: 'red';
  width: 60%;
  overflow-wrap: anywhere;
`;

const CheckboxFields = styled(FormGroup)`
  display: flex;
  flex-direction: column;
  width: 95%;
  max-height: 300px;
  flex-wrap: nowrap;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.palette.grey[600]} ${({ theme }) => theme.palette.background.default};
`;
const CheckboxField = styled('div', { shouldForwardProp: (prop) => prop !== 'checked' })<{ checked: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 100px;
  padding: 0 1rem;
  border-radius: 5px;
  ${({ theme, checked }) => css`
    background-color: ${checked ? theme.palette.primary.main : 'inherit'};
    color: ${checked ? theme.palette.primary.contrastText : 'inherit'};
    svg {
      color: ${checked ? theme.palette.primary.contrastText : 'inherit'};
    }
  `}
`;

const FileSizeLabel = styled(Typography)`
  font-weight: bold;
  padding: 0 1rem;
  flex-grow: 0.1;
  color: ${({ theme }) => theme.palette.grey[900]};
`;

const FileSize = styled(Typography)`
  flex-grow: 0.3;
`;

const TitleWithSubTitle = styled('div')`
  display: flex;
  flex-direction: column;
`;

const Title = styled(Typography)`
  position: static;
  font-weight: bold;
  width: 10%;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.grey[900]};
`;

const FormDivider = styled(Divider)`
  width: 95%;
  height: 2px;
`;
